import type { UpstreamApiDashboardResult } from 'src/types/dashboard';
import { numberOrDash } from 'src/helper/helper';

export type participationChannelField =
  | 'returns_particip_email'
  | 'returns_particip_qr_code'
  | 'returns_particip_code'
  | 'returns_particip_sc'
  | 'returns_particip_zebra';

export type participationChannelRateField =
  | 'returns_particip_email_rate'
  | 'returns_particip_qr_code_rate'
  | 'returns_particip_code_rate'
  | 'returns_particip_sc_rate'
  | 'returns_particip_zebra_rate';

export const PARTICIPATION_CHANNEL_FIELDS: participationChannelField[] = [
  'returns_particip_email',
  'returns_particip_qr_code',
  'returns_particip_code',
  'returns_particip_sc',
  'returns_particip_zebra',
];

const ratioOfReturnsOrDash = (
  datalineItem: number | null | undefined,
  returnItem: number | null,
) => {
  // allow 0% for datalineItem being 0
  if (!returnItem || (datalineItem !== 0 && !datalineItem)) {
    return '--';
  }

  return `${Math.round((100 * datalineItem) / returnItem)}%`;
};

interface TransformedDashboardResultData {
  labels: string[];
  rates: (number | null)[];
  diffs: (number | null)[];
  respondents: (number | null)[];
  invited: number[];
  returns_particip_email: (string | null)[];
  returns_particip_qr_code: (string | null)[];
  returns_particip_code: (string | null)[];
  returns_particip_sc: (string | null)[];
  returns_particip_zebra: (string | null)[];
  returns_particip_email_rate: (string | null)[];
  returns_particip_qr_code_rate: (string | null)[];
  returns_particip_code_rate: (string | null)[];
  returns_particip_sc_rate: (string | null)[];
  returns_particip_zebra_rate: (string | null)[];
}

export const transformData = (data: UpstreamApiDashboardResult[]) => {
  const result: TransformedDashboardResultData = {
    labels: [],
    rates: [],
    diffs: [],
    respondents: [],
    invited: [],
    returns_particip_email: [],
    returns_particip_qr_code: [],
    returns_particip_code: [],
    returns_particip_sc: [],
    returns_particip_zebra: [],
    returns_particip_email_rate: [],
    returns_particip_qr_code_rate: [],
    returns_particip_code_rate: [],
    returns_particip_sc_rate: [],
    returns_particip_zebra_rate: [],
  };
  // optional field availability should be consistent for all datalines in a survey
  let participationFields: participationChannelField[] = [];
  if (data.length && data[0]?.dataline) {
    participationFields = PARTICIPATION_CHANNEL_FIELDS.filter(
      (field) => field in data[0].dataline,
    );
  }
  data.forEach(({ dataline, org_units: [{ name }] }) => {
    result.labels.push(name);
    result.rates.push(dataline.response_rate);
    result.diffs.push(dataline.response_rate_yoyDiff);
    result.respondents.push(dataline.returns);
    result.invited.push(dataline.headcount);

    participationFields.forEach((field) => {
      result[field].push(numberOrDash(dataline[field]));
      const fieldRateName: participationChannelRateField = `${field}_rate`;
      result[fieldRateName].push(
        ratioOfReturnsOrDash(dataline[field], dataline.returns),
      );
    });
  });
  return result;
};
